import { InstallAdmin } from '../install/InstallAdmin.js';
import type { AdminRole } from '@diana/contracts';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link, NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';

import { useShellPublishing } from '../../app/ShellPublishingContext.js';
import {
  readSettingsSection,
  settingsSectionSearch,
  websiteSections,
  type SettingsSection,
} from './settings-sections.js';
import './SettingsWorkspace.css';

export function SettingsWorkspace({ role }: { role: AdminRole }) {
  const { user, logout } = useShellPublishing();
  const location = useLocation();
  const latestLocation = useRef(location);
  latestLocation.current = location;
  const navigate = useNavigate();
  const [wide, setWide] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const blur = () => {
      if (
        document.activeElement instanceof HTMLElement &&
        document.activeElement.closest('.settings-workspace')
      )
        document.activeElement.blur();
    };
    const update = () => {
      blur();
      setWide(media.matches);
    };
    window.addEventListener('popstate', blur);
    update();
    media.addEventListener('change', update);
    return () => {
      media.removeEventListener('change', update);
      window.removeEventListener('popstate', blur);
    };
  }, []);
  const website = useMatch('/settings') !== null;
  const calendar = useMatch('/settings/calendar/*') !== null;
  const users = useMatch('/settings/users') !== null;
  const requested = readSettingsSection(new URLSearchParams(location.search).get('section'));
  const section = requested ?? (wide ? 'contacts' : null);
  const hub = website && section === null;

  const sectionLink = (next: SettingsSection | null) => ({
    pathname: '/settings',
    search: settingsSectionSearch(location.search, next),
    hash: location.hash,
  });
  const select = (event: MouseEvent<HTMLAnchorElement>, next: SettingsSection | null) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    event.preventDefault();
    // Commit native blur-only placement text before its mounted section becomes hidden.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    const current = latestLocation.current;
    const search = settingsSectionSearch(current.search, next);
    latestLocation.current = { ...current, pathname: '/settings', search };
    navigate({ pathname: '/settings', search, hash: current.hash });
  };

  return (
    <article
      className={`editor editor--wide settings-workspace${!wide && !hub ? ' settings-workspace--active' : ''}`}
    >
      <h1 className="settings-workspace__sr-heading" hidden={wide || hub}>
        Settings
      </h1>
      <header className="settings-workspace__header" hidden={!wide && !hub}>
        <Link
          className="dui-back-link settings-workspace__site-back"
          aria-label="Back to Site"
          to="/site"
        >
          <span aria-hidden="true">‹</span>
          <span>Back to Site</span>
        </Link>
        <h1>Settings</h1>
      </header>
      {!wide && !hub ? (
        <div className="settings-workspace__active-header">
          <Link
            className="dui-back-link"
            aria-label="Back to Settings"
            to={sectionLink(null)}
            onClick={(event) => select(event, null)}
          >
            <span aria-hidden="true">‹</span>
          </Link>
          {website ? (
            <h2>
              {section === 'mac'
                ? 'Mac app'
                : websiteSections.find((item) => item.id === section)?.label}
            </h2>
          ) : calendar ? (
            <h2>Google Calendar</h2>
          ) : users ? (
            <h2>Users</h2>
          ) : null}
        </div>
      ) : null}
      <div className="settings-workspace__layout">
        <nav
          className="settings-workspace__nav"
          aria-label="Settings sections"
          hidden={!wide && !hub}
        >
          <div className="settings-account" aria-label="Your account">
            <span className="settings-account__initial" aria-hidden="true">
              {user.email.slice(0, 1).toUpperCase()}
            </span>
            <div>
              <strong>{user.email}</strong>
              <span>{user.role}</span>
            </div>
          </div>
          <section className="settings-workspace__group">
            <h2>Website</h2>
            <div className="settings-workspace__rows">
              {websiteSections.map((item) => (
                <Link
                  key={item.id}
                  aria-label={item.label}
                  to={sectionLink(item.id)}
                  onClick={(event) => select(event, item.id)}
                  aria-current={website && section === item.id ? 'page' : undefined}
                >
                  <span>
                    {item.label}
                    <small>{item.description}</small>
                  </span>
                  <span aria-hidden="true">›</span>
                </Link>
              ))}
            </div>
          </section>
          {role === 'Owner' ? (
            <section className="settings-workspace__group">
              <h2>People and integrations</h2>
              <div className="settings-workspace__rows">
                <NavLink aria-label="Google Calendar" to="/settings/calendar">
                  <span>
                    Google Calendar<small>Shoot schedule sync</small>
                  </span>
                  <span aria-hidden="true">›</span>
                </NavLink>
                <NavLink aria-label="Users" to="/settings/users">
                  <span>
                    Users<small>Access and invitations</small>
                  </span>
                  <span aria-hidden="true">›</span>
                </NavLink>
              </div>
            </section>
          ) : null}
          <section className="settings-workspace__group">
            <h2>Library and app</h2>
            <div className="settings-workspace__rows">
              <InstallAdmin />
              <NavLink aria-label="Media" to="/settings/media">
                <span>
                  Media<small>Website image library</small>
                </span>
                <span aria-hidden="true">›</span>
              </NavLink>
              <Link
                aria-label="Mac app"
                to={sectionLink('mac')}
                onClick={(event) => select(event, 'mac')}
                aria-current={website && section === 'mac' ? 'page' : undefined}
              >
                <span>
                  Mac app<small>Gallery upload setup</small>
                </span>
                <span aria-hidden="true">›</span>
              </Link>
            </div>
          </section>
          <button className="settings-workspace__logout" type="button" onClick={logout}>
            Sign out
          </button>
        </nav>
        <div className="settings-workspace__content">
          <Outlet context={{ section }} />
        </div>
      </div>
    </article>
  );
}
